import { useContext, useEffect, useRef } from 'react';
import { EventEmitterContext } from '@visx/xychart';

import { useDragToZoom } from '../../../utils/useDragToZoom';

interface DragToZoomHandlerProps {
  containerId: string;
}

/**
 * Component that calls useDragToZoom hook and subscribes to visx event emitter events.
 * Must be rendered inside XYChart to access DataContext and EventEmitterContext.
 */
export function DragToZoomHandler({ containerId: _containerId }: DragToZoomHandlerProps) {
  const { selectionRect, coordinateHandlers } = useDragToZoom();
  const eventEmitter = useContext(EventEmitterContext);
  const handlersRef = useRef(coordinateHandlers);

  // Keep handlers ref up to date
  useEffect(() => {
    handlersRef.current = coordinateHandlers;
  }, [coordinateHandlers]);

  // Subscribe to visx event emitter events
  useEffect(() => {
    if (!eventEmitter) {
      return;
    }

    // Create wrapper functions that use the latest handlers and pass coordinates directly
    const handlePointerDown = (event?: { svgPoint?: { x: number; y: number } }) => {
      if (!event?.svgPoint) {
        return;
      }
      handlersRef.current.onPointerDown(event.svgPoint);
    };

    const handlePointerMove = (event?: { svgPoint?: { x: number; y: number } }) => {
      if (!event?.svgPoint) {
        return;
      }
      handlersRef.current.onPointerMove(event.svgPoint);
    };

    const handlePointerUp = () => {
      handlersRef.current.onPointerUp();
    };

    const handlePointerOut = () => {
      handlersRef.current.onPointerLeave();
    };

    // Subscribe to pointer events from visx
    eventEmitter.on('pointerdown', handlePointerDown);
    eventEmitter.on('pointermove', handlePointerMove);
    eventEmitter.on('pointerup', handlePointerUp);
    eventEmitter.on('pointerout', handlePointerOut);

    return () => {
      eventEmitter.off('pointerdown', handlePointerDown);
      eventEmitter.off('pointermove', handlePointerMove);
      eventEmitter.off('pointerup', handlePointerUp);
      eventEmitter.off('pointerout', handlePointerOut);
    };
  }, [eventEmitter]);

  return (
    <>
      {/* SVG rect must be rendered directly as child of XYChart (which renders SVG) */}
      {selectionRect}
    </>
  );
}
